import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import './navbar.css';

// Animation variants for Framer Motion
const linkVariants = {
  rest: {
    scale: 1,
    color: "var(--text-primary)",
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  },
  hover: {
    scale: 1.05,
    color: "var(--primary)",
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

const highlightVariants = {
  rest: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  },
  hover: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

const mobileMenuVariants = {
  closed: {
    opacity: 0,
    x: "100%",
    transition: {
      duration: 0.3
    }
  },
  open: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3
    }
  }
};

function Navbar() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);

  // Navigation items with proper links
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'About', path: '/aboutus' },
    { name: 'Contact', path: '/contactus' }
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      if (scrollTop > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => {
    // Scroll to top when navigating
    window.scrollTo(0, 0);
    // Close mobile menu if open
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target) && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Check if a nav item is active
  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <motion.nav 
      className={`navbar ${isScrolled ? 'scrolled' : ''}`} 
      ref={navRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", damping: 15, stiffness: 100 }}
    >
      <div className="nav-container">
        {/* Logo/Brand */}
        <Link 
          to="/" 
          className="nav-brand"
          onClick={handleNavClick}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="brand-container"
          >
            <span className="brand-text">LEARNEX</span>
            <span className="brand-tagline">IT Solutions</span>
          </motion.div>
        </Link>

        {/* Desktop Navigation */}
        <ul className="nav-list">
          {navItems.map((item, index) => (
            <li 
              key={index} 
              className="nav-item"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <Link
                to={item.path}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <motion.span
                  variants={linkVariants}
                  initial="rest"
                  whileHover="hover"
                  animate="rest"
                >
                  {item.name}
                </motion.span>
                {/* Animated highlight bar underneath */}
                <motion.span 
                  className="highlight"
                  variants={highlightVariants}
                  style={{ 
                    left: hoveredIndex === index ? '0%' : '100%',
                    width: hoveredIndex === index ? '100%' : '0%'
                  }}
                />
              </Link>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <Link to="/contactus" onClick={handleNavClick}>
          <motion.button 
            className="nav-cta-btn"
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0 5px 15px rgba(37, 99, 235, 0.3)" 
            }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started
          </motion.button>
        </Link>

        {/* Mobile Menu Button */}
        <button 
          className={`mobile-menu-btn ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <motion.div 
        className="mobile-menu"
        initial="closed"
        animate={isMobileMenuOpen ? "open" : "closed"}
        variants={mobileMenuVariants}
      >
        <div className="mobile-menu-content">
          <div className="mobile-menu-header">
            <span className="mobile-brand">LEARNEX</span>
            <button 
              className="mobile-close-btn"
              onClick={toggleMobileMenu}
              aria-label="Close menu"
            >
              &times;
            </button>
          </div>
          
          <div className="mobile-nav-items">
            {navItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <motion.span
                  whileHover={{ x: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {item.name}
                </motion.span>
              </Link>
            ))}
          </div>
          
          <div className="mobile-menu-footer">
            <Link to="/contactus" onClick={handleNavClick}>
              <motion.button 
                className="mobile-cta-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Contact Us
              </motion.button>
            </Link>
            
            <div className="mobile-contact-info">
              <p>+94 70 1111 735</p>
              <p>info@learnex.lk</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <motion.div 
          className="mobile-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={toggleMobileMenu}
        />
      )}
    </motion.nav>
  );
}

export default Navbar;